import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetSystemConfig,
  GetSystemConfigList,
  CreateSystemConfig,
  UpdateSystemConfig,
  DeleteSystemConfig,
} from "../Api";
import {
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const systemConfigQueryKeys = {
  systemConfig: () => ["systemConfig"] as const,
  systemConfigList: () => ["systemConfigList"] as const,
};

export const invalidateSystemConfigQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["systemConfig"] });
  queryClient.invalidateQueries({ queryKey: ["systemConfigList"] });
};

export const useSystemConfigQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: systemConfigQueryKeys.systemConfig(),
    queryFn: async () => unwrapApiResponse(await GetSystemConfig()),
  });
};

export const useSystemConfigRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: systemConfigQueryKeys.systemConfig(),
        queryFn: async () => unwrapApiResponse(await GetSystemConfig()),
      }),
  });
};

export const useSystemConfigListQuery = (
  tenantId?: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: systemConfigQueryKeys.systemConfigList(),
    queryFn: async () =>
      unwrapApiResponse(await GetSystemConfigList({ tenant_id: tenantId })),
  });
};

export const useCreateSystemConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateSystemConfig>[0]) =>
      CreateSystemConfig(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateSystemConfigQueries(queryClient);
      }
    },
  });
};

export const useUpdateSystemConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateSystemConfig>[0]) =>
      UpdateSystemConfig(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateSystemConfigQueries(queryClient);
      }
    },
  });
};

export const useDeleteSystemConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => DeleteSystemConfig(id),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateSystemConfigQueries(queryClient);
      }
    },
  });
};

export const useSystemConfigService = (config?: {
  tenantId?: string;
  fetchSystemConfigEnabled?: boolean;
}) => {
  const systemConfigQuery = useSystemConfigQuery({
    enabled: config?.fetchSystemConfigEnabled !== false,
  });

  const createMutation = useCreateSystemConfigMutation();
  const updateMutation = useUpdateSystemConfigMutation();
  const deleteMutation = useDeleteSystemConfigMutation();

  return {
    systemConfigQuery,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
